/**
 * `studio/analytics.html` §1 — recurring revenue, and what moved it.
 *
 * Two charts side by side in one region (CONTRACT.md §4.4: borders separate,
 * whitespace groups), each with the `role="img"`, the conclusion and the table view
 * §2.13 makes non-negotiable. The tables are `tables.ts`', so the export prints the
 * same rows.
 *
 * ## The movements chart draws what was added, and says so
 *
 * The mockup draws new and expansion above a zero line and churn **below** it.
 * `ColumnChart` cannot: it maps `0` to the baseline and the largest tick to the top
 * of the plot, so a negative value lands off the bottom of the `viewBox` and a
 * negative tick lands outside it altogether. `ZeroLine` exists in `Chart.tsx` for
 * exactly this chart and has nothing to sit under.
 *
 * The two ways out were to hand-draw the bars in this file — which is a page writing
 * SVG geometry, the thing session 09's primitives exist to stop — or to chart the
 * part that stacks and put the rest where it is still reachable. This does the
 * second: the columns are new + expansion, the legend and the conclusion say
 * "added", the narration line names every month that **lost** recurring revenue and
 * what it lost, and the table view carries all five figures per month including
 * contraction, which the mockup's own table leaves out. The report asks for negative
 * support on `ColumnChart`.
 */

import { formatList, sum } from '@fareast/shared'

import {
  Chart,
  ColumnChart,
  COLUMN_VIEW_BOX,
  Legend,
  LegendKey,
  LineChart,
  LINE_VIEW_BOX,
} from '../../../components/portal/Chart.js'
import { Section, SectionHead } from '../../../components/portal/Layout.js'
import { Region, RegionCols } from '../../../components/portal/Region.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import { currencyOf, moneyTicks } from './axis.js'
import type { AnalyticsReads, MrrData } from './data.js'
import { Panel } from './Panel.js'
import { shortMonth, WHOLE_AMOUNT } from './render.js'
import { mrrMovementsTable, mrrSeriesTable, onScreen } from './tables.js'

export function MrrSection({ query }: { readonly query: AnalyticsReads['mrr'] }) {
  const format = useFormat()
  const mrr = query.data

  return (
    <Section>
      <SectionHead
        title="Recurring revenue"
        note={mrr === undefined ? undefined : rangeNote(mrr, format)}
        pushNote
      />
      <Region>
        <Panel query={query} subject="the recurring revenue series">
          {(data) => (
            <RegionCols>
              <SeriesChart mrr={data} />
              <MovementsChart mrr={data} />
            </RegionCols>
          )}
        </Panel>
      </Region>
    </Section>
  )
}

/** "January – August 2026 · USD equivalents", from the series' own two ends. */
function rangeNote(mrr: MrrData, format: Formatter): string {
  const first = mrr.series[0]
  const last = mrr.series.at(-1)
  if (first === undefined || last === undefined) return 'Nothing recorded yet'
  const currency = currencyOf(mrr.series.map((one) => one.mrr))
  return `${format.monthLong(first.month)} – ${format.monthLong(last.month)} · ${currency} equivalents`
}

/* ------------------------------------------------------------- the MRR series */

function SeriesChart({ mrr }: { readonly mrr: MrrData }) {
  const format = useFormat()
  const currency = currencyOf(mrr.series.map((one) => one.mrr))
  const peak = Math.max(...mrr.series.map((one) => one.mrr.amount), 0)

  const first = mrr.series[0]
  const last = mrr.series.at(-1)
  const conclusion =
    first === undefined || last === undefined
      ? 'No recurring revenue has been recorded yet.'
      : `Monthly recurring revenue from ${format.money(first.mrr, WHOLE_AMOUNT)} in ${format.month(first.month)} to ${format.money(last.mrr, WHOLE_AMOUNT)} in ${format.month(last.month)}.`

  return (
    <Chart
      conclusion={conclusion}
      viewBox={LINE_VIEW_BOX}
      note="Every live retainer's monthly amount, converted and read on the last day of the month."
      table={onScreen(mrrSeriesTable(mrr, format))}
    >
      <LineChart
        points={mrr.series.map((point) => ({
          label: shortMonth(point.month, format),
          value: point.mrr.amount,
        }))}
        ticks={moneyTicks(peak, currency, format)}
        area
        slot="c3"
      />
    </Chart>
  )
}

/* ------------------------------------------------------------------ movements */

function MovementsChart({ mrr }: { readonly mrr: MrrData }) {
  const format = useFormat()
  const currency = currencyOf(mrr.movements.map((one) => one.net))

  const added = mrr.movements.filter((one) => one.new.amount !== 0 || one.expansion.amount !== 0)
  const lost = mrr.movements.filter((one) => one.churn.amount !== 0 || one.contraction.amount !== 0)
  const peak = Math.max(
    ...mrr.movements.map((one) => sum([one.new, one.expansion], currency).amount),
    0,
  )

  return (
    <Chart
      conclusion={
        added.length === 0
          ? 'No recurring revenue was added in these months.'
          : `Recurring revenue added by month: ${added
              .map((one) => addedPhrase(one, format))
              .join('; ')}.`
      }
      viewBox={COLUMN_VIEW_BOX}
      legend={
        <Legend>
          <LegendKey slot="c2">New</LegendKey>
          <LegendKey slot="c3">Expansion</LegendKey>
        </Legend>
      }
      note={lostNote(lost, format)}
      table={onScreen(mrrMovementsTable(mrr, format))}
    >
      <ColumnChart
        data={mrr.movements.map((one) => ({
          label: shortMonth(one.month, format),
          value: [one.new.amount, one.expansion.amount],
          title: movementTitle(one, format),
        }))}
        ticks={moneyTicks(peak, currency, format)}
        slots={['c2', 'c3']}
      />
    </Chart>
  )
}

type Movement = MrrData['movements'][number]

/** "March new $3,180" / "June new $890 and expansion $510" — one clause per month. */
function addedPhrase(one: Movement, format: Formatter): string {
  const parts = [
    one.new.amount === 0 ? null : `new ${format.money(one.new, WHOLE_AMOUNT)}`,
    one.expansion.amount === 0 ? null : `expansion ${format.money(one.expansion, WHOLE_AMOUNT)}`,
  ].filter((part): part is string => part !== null)
  return `${format.month(one.month)} ${formatList(parts)}`
}

/** The `<title>` a pointer reveals — every figure the month has, lost ones included. */
function movementTitle(one: Movement, format: Formatter): string {
  return `${format.monthLong(one.month)}: new ${format.money(one.new, WHOLE_AMOUNT)}, expansion ${format.money(one.expansion, WHOLE_AMOUNT)}, contraction ${format.money(one.contraction, WHOLE_AMOUNT)}, churn ${format.money(one.churn, WHOLE_AMOUNT)} — net ${format.money(one.net, WHOLE_AMOUNT)}`
}

/**
 * The one narration line, and it is the chart's own caveat.
 *
 * The columns are what was added; a month that lost recurring revenue leaves no mark
 * on them at all, so the line names each one and what it lost, and points at the
 * table underneath where the whole of it is.
 */
function lostNote(lost: readonly Movement[], format: Formatter): string {
  if (lost.length === 0) {
    return 'The bars are what was added. Nothing was lost in these months — no cancellation and no package cut.'
  }
  const months = formatList(
    lost.map(
      (one) =>
        `${format.month(one.month)} ${format.money(sum([one.churn, one.contraction], one.net.currency), WHOLE_AMOUNT)}`,
    ),
  )
  return `The bars are what was added; what was lost is in the table. ${months} — ${lost.length === 1 ? 'one month' : `${format.number(lost.length)} months`} of churn or contraction.`
}
